import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { FeatureWithActions, KanbanColumn as KanbanColumnType } from '@/shared/types'
import { FeatureCard } from './feature-card'
import { cn } from '@/shared/lib/utils'

interface KanbanColumnProps {
  column: KanbanColumnType
  features: FeatureWithActions[]
  onFeatureClick?: (feature: FeatureWithActions) => void
}

const COLUMN_COLORS: Record<KanbanColumnType, string> = {
  backlog: 'bg-slate-500',
  scoping: 'bg-violet-500',
  design: 'bg-pink-500',
  development: 'bg-amber-500',
  testing: 'bg-blue-500',
  done: 'bg-green-500',
}

// WIP limit = 1 for all columns except Backlog and Done
const WIP_LIMITS: Partial<Record<KanbanColumnType, number>> = {
  scoping: 1,
  design: 1,
  development: 1,
  testing: 1,
}

// Features completed within this many days are shown by default
const DONE_VISIBLE_DAYS = 7

function isRecentlyDone(feature: FeatureWithActions): boolean {
  if (!feature.done_at) return true // If no done_at, show it
  const doneDate = new Date(feature.done_at)
  const cutoffDate = new Date()
  cutoffDate.setDate(cutoffDate.getDate() - DONE_VISIBLE_DAYS)
  return doneDate > cutoffDate
}

export function KanbanColumn({ column, features, onFeatureClick }: KanbanColumnProps) {
  const { t } = useTranslation()
  const [showAllDone, setShowAllDone] = useState(false)

  // For Done column, separate recent and old features
  const isDoneColumn = column === 'done'
  const recentFeatures = isDoneColumn ? features.filter(isRecentlyDone) : features
  const oldFeatures = isDoneColumn ? features.filter(f => !isRecentlyDone(f)) : []
  const hasOldFeatures = oldFeatures.length > 0

  // Displayed features based on toggle
  const displayedFeatures = isDoneColumn
    ? (showAllDone ? features : recentFeatures)
    : features

  // Sort Done features by done_at (most recent first)
  if (isDoneColumn) {
    displayedFeatures.sort((a, b) => {
      const dateA = a.done_at ? new Date(a.done_at).getTime() : 0
      const dateB = b.done_at ? new Date(b.done_at).getTime() : 0
      return dateB - dateA
    })
  }

  const count = features.length
  const wipLimit = WIP_LIMITS[column]
  const hasWipLimit = wipLimit !== undefined
  const isAtLimit = hasWipLimit && count >= wipLimit

  return (
    <div className={cn(
      'flex h-full w-[280px] flex-shrink-0 flex-col gap-3 rounded-xl p-4',
      isAtLimit ? 'bg-orange-50' : 'bg-background'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn('h-2.5 w-2.5 rounded', COLUMN_COLORS[column])} />
          <span className="text-sm font-semibold text-foreground">
            {t(`kanban.columns.${column}`)}
          </span>
        </div>

        {/* Counter with optional WIP limit */}
        <div className="flex items-center gap-1">
          <span className={cn(
            'text-[13px] font-medium',
            isAtLimit ? 'text-amber-600' : 'text-foreground-muted'
          )}>
            {count}
          </span>
          {hasWipLimit && (
            <span className="text-xs text-foreground-muted/70">
              / {wipLimit}
            </span>
          )}
        </div>
      </div>

      {/* Cards */}
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
        {displayedFeatures.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-foreground-muted">
              {t('kanban.empty')}
            </p>
          </div>
        ) : (
          displayedFeatures.map((feature) => (
            <FeatureCard
              key={feature.id}
              feature={feature}
              onClick={() => onFeatureClick?.(feature)}
            />
          ))
        )}

        {/* View all / Hide button for Done column */}
        {isDoneColumn && hasOldFeatures && (
          <button
            onClick={() => setShowAllDone(!showAllDone)}
            className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-border py-2 text-xs font-medium text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground"
          >
            {showAllDone ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                {t('kanban.hideOlder')}
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                {t('kanban.viewAll', { count: oldFeatures.length })}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}
